/**
 * MapServicesDashboard
 * Dashboard med kort-layout för kartjänster/geodata-funktioner under Deque
 */

import { Link, useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants/routes';
import { withBackTo } from '@/utils/navigationBack';
import { useTranslation } from '@/store/languageStore';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/store/authStore';
import ModuleFavoriteStar from '@/components/common/ModuleFavoriteStar';
import {
  MapIcon,
  MapPinIcon,
  FireIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

const MapServicesDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasFeatureAccess } = usePermissions();

  const isAdmin = user?.is_global_admin === true;
  const canSee = (code: string) => isAdmin || hasFeatureAccess(code);

  return (
    <div className="p-4 bg-bgContainer rounded-lg h-full">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(ROUTES.SYSTEM_DEQUE)}
          className="p-2 hover:bg-bgElevated rounded-lg transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 text-textDefault" />
        </button>
        <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
          <MapIcon className="w-6 h-6 text-primary" />
          {t('mapServices.title') || 'Kartjänster'}
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {canSee('ROUTE_ANALYSIS') && (
          <Link
            to={ROUTES.DEQUE_TRACK}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="map-services/track" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-cyan-100 dark:bg-cyan-900/30 rounded-lg">
                <MapIcon className="w-8 h-8 text-cyan-600 dark:text-cyan-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('mapServices.track') || 'Track'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('mapServices.trackDescription') || 'Historisk GPS-spårning med playback för flera fordon'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('ADMIN_GEOFENCES') && (
          <Link
            to={ROUTES.ADMIN_GEOFENCES}
            {...withBackTo(ROUTES.DEQUE_MAP_SERVICES)}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="map-services/geofence" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-rose-100 dark:bg-rose-900/30 rounded-lg">
                <MapPinIcon className="w-8 h-8 text-rose-600 dark:text-rose-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('mapServices.geofence') || 'Geofence'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('mapServices.geofenceDescription') || 'Hantera geografiska zoner och områdesövervakning'}
                </p>
              </div>
            </div>
          </Link>
        )}

        {canSee('ANALYSIS_HEATMAP') && (
          <Link
            to={ROUTES.ANALYSIS_HEATMAP}
            {...withBackTo(ROUTES.DEQUE_MAP_SERVICES)}
            className="relative p-6 bg-bgElevated rounded-lg border border-border hover:border-bgPrimary transition-colors"
          >
            <ModuleFavoriteStar systemName="deque" moduleName="map-services/heatmap" />
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <FireIcon className="w-8 h-8 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-textDefault">
                  {t('mapServices.heatmap') || 'Heatmap'}
                </h2>
                <p className="text-sm text-textSecondary mt-1">
                  {t('mapServices.heatmapDescription') || 'Visualisera aktivitetsmönster som värmekarta'}
                </p>
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};

export default MapServicesDashboard;
